import React, {useState, useEffect} from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import {ThemeProvider} from '@mui/material/styles';
import {Link, useLocation} from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { colors } from '../components/Theme/WebTheme';
import { useTranslation } from "react-i18next";
import {MAIN_COLOR, SECONDORY_COLOR, FONT_FAMILY} from "../common/sharedFunctions"
import { getMenuItems } from '../config/menuConfig';
import { ref, get, getDatabase } from 'firebase/database';
import adminTheme from './Theme/AdminTheme';



const drawerWidth = 260;

export default function ResponsiveDrawer(props) {
  const { t, i18n  } = useTranslation();
  const isRTL = i18n.dir();
  const location = useLocation();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const {
    signOff
  } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();

  const LogOut = () => {
    dispatch(signOff());
  };

  const [role, setRole] = useState(null);
  const [managerPermissions, setManagerPermissions] = useState({});
  const database = getDatabase();

  useEffect(() => {
    if(auth.profile && auth.profile.usertype){
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  // Load manager permissions when user is a manager
  useEffect(() => {
    const loadManagerPermissions = async () => {
      if (auth.profile && auth.profile.role === 'manager') {
        try {
          const managerRef = ref(database, "roles/manager/permissions");
          const snapshot = await get(managerRef);
          if (snapshot.exists()) {
            setManagerPermissions(snapshot.val());
          }
        } catch (error) {
          console.error("Error loading manager permissions:", error);
        }
      }
    };

    loadManagerPermissions();
  }, [auth.profile, database]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDrawer = () => {
    setMobileOpen(false);
  };

  const isActiveMenuItem = (item) => item.url !== 'logout' &&
    (location.pathname === item.url ||
      (item.url !== '/' && location.pathname.startsWith(`${item.url}/`)));

  // Check if user has permission for a specific menu item
  const hasPermission = (item) => {
    // Logout should always be available to all authenticated users
    if (item.key === 'logout') {
      return true;
    }
    
    // If user is not Manager role, use existing access logic
    if (!auth.profile || auth.profile.role !== 'manager') {
      return item.access.includes(role);
    }
    
    // For Manager role, check database permissions
    const hasMainPermission = managerPermissions[item.key] === true;
    
    // If item has sub-permissions, check if any sub-permission is enabled
    if (item.sub_permissions && !hasMainPermission) {
      return Object.keys(item.sub_permissions).some(subKey => 
        managerPermissions[`${item.key}_${subKey}`] === true
      );
    }
    
    return hasMainPermission;
  };

  const drawer = (
    <div style={{backgroundColor: '#111411', height: '100%'}}>
      <div style={{
        alignItems: 'center',
        borderBottom: '1px solid rgba(183, 238, 0, 0.22)',
        display: 'flex',
        justifyContent: 'center',
        minHeight: 132,
      }}>
        <div style={{
          alignItems: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: 18,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.24)',
          display: 'flex',
          justifyContent: 'center',
          padding: 10,
        }}>
          <img
            style={{display: 'block', width: 92, height: 92, objectFit: 'contain'}}
            src={require("../assets/img/logo192x192.png")}
            alt="Laiwani"
          />
        </div>
      </div>
      <div style={{backgroundColor: '#111411', padding: '10px 8px 20px'}}>
        {role ?
        <List disablePadding={true} dense={false}>
          {getMenuItems(t).map((item) => (
            hasPermission(item)?
              <div style={{display: 'flex', minHeight: 48}} key={item.key}>
                <ListItem disableGutters={true} disablePadding={true} alignItems='center' dense={false} onClick={handleDrawer}>
                  <ListItemButton
                    component={Link}
                    to={item.url === 'logout' ? '#' : item.url}
                    onClick={item.url === 'logout' ? LogOut : null}
                    selected={isActiveMenuItem(item)}
                    sx={{
                      borderRadius: '10px',
                      minHeight: 44,
                      px: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: '#B7EE00',
                        color: '#191919',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: '#C5FF0A',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(183, 238, 0, 0.12)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: isActiveMenuItem(item) ? '#191919' : colors.Header_Text,
                      minWidth: 40,
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText inset={false} disableTypography={false} 
                    primary={<Typography style={{
                      color: isActiveMenuItem(item) ? '#191919' : colors.Header_Text,
                      fontFamily: FONT_FAMILY,
                      fontWeight: isActiveMenuItem(item) ? 700 : 500,
                      textAlign: isRTL === 'rtl' ? 'right' : 'left',
                    }}>
                    {item.name}
                  </Typography>} 
                   />
                  </ListItemButton>
                </ListItem>
              </div>
            :null
          ))}
        </List>
        : null }

      </div>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <ThemeProvider theme={adminTheme}>
    <Box sx={{display: 'flex', direction: isRTL === 'rtl' ? 'rtl' : 'ltr'}}>
      <CssBaseline />
      <style>
        {`
          ::-webkit-scrollbar {
            width: 8px;
          }

          ::-webkit-scrollbar-thumb {
            background-color: ${SECONDORY_COLOR};
            border-radius: 6px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background-color: ${MAIN_COLOR};
          }

          ::-webkit-scrollbar-track {
            background-color:${colors.WHITE};
          }
        `}
      </style>
      <AppBar
        position="fixed"
        style={isRTL === 'rtl' ?
          {marginRight: drawerWidth, backgroundColor: '#111411'} :
          {marginLeft: drawerWidth, backgroundColor: '#111411'}}
        sx={{
          borderBottom: '2px solid #B7EE00',
          boxShadow: 'none',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color={colors.WHITE}
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2,  display: { sm: 'none' }, }}
          >
            <MenuIcon style={{color:colors.WHITE}} />
          </IconButton>
          <Typography variant="h6" noWrap component="div" style={{color:colors.WHITE, fontFamily:FONT_FAMILY,}}>
          {settings && settings.appName? settings.appName: '' }
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },direction:isRTL === 'rtl'? 'rtl':'ltr'
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },direction:isRTL === 'rtl'? 'rtl':'ltr'
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          backgroundColor: '#F5F7F2',
          flexGrow: 1,
          minHeight: '100vh',
          p: {xs: 1.5, md: 3},
          width: {sm: `calc(100% - ${drawerWidth}px)`},
        }}
      >
        <Toolbar/>
        {props.children}
      </Box>
    </Box>
    </ThemeProvider>
  );
}
